/* <facet-form>: live filtering and sorting for the collection and search pages.
   Markup comes from snippets/facets.liquid. Without this script the filter form is a
   plain GET form with an Apply button, so everything here is an enhancement.
   - Any filter checkbox or the sort select: re-render at once.
   - Price inputs: re-render 500ms after typing stops.
   - Pills, Clear all and Clear filters links: re-render instead of navigating.
   The section is fetched with the Section Rendering API (section_id) and every
   [data-facet-region] is swapped from the response. History is kept with pushState. */

(() => {
  if (customElements.get('facet-form')) return;

  const DESKTOP = window.matchMedia('(min-width: 990px)');

  class FacetForm extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.cache = new Map();
      this.controller = null;
      this.priceTimer = null;

      this.onChange = this.onChange.bind(this);
      this.onInput = this.onInput.bind(this);
      this.onSubmit = this.onSubmit.bind(this);
      this.onClick = this.onClick.bind(this);
      this.onPopState = this.onPopState.bind(this);
      this.syncMode = this.syncMode.bind(this);

      this.addEventListener('change', this.onChange);
      this.addEventListener('input', this.onInput);
      this.addEventListener('submit', this.onSubmit);
      this.addEventListener('click', this.onClick);
      window.addEventListener('popstate', this.onPopState);
      DESKTOP.addEventListener('change', this.syncMode);

      history.replaceState({ ...(history.state || {}), facets: true }, '', window.location.href);
      this.syncMode();
    }

    disconnectedCallback() {
      window.removeEventListener('popstate', this.onPopState);
      DESKTOP.removeEventListener('change', this.syncMode);
    }

    get form() {
      return this.querySelector('form[data-facet-form]');
    }

    get drawer() {
      return this.querySelector('.facets-drawer');
    }

    /* The filter panel is a modal drawer below 990px and a plain rail above it. */
    syncMode() {
      const drawer = this.drawer;
      if (!drawer) return;
      const panel = drawer.querySelector('[data-facets-panel]');
      if (DESKTOP.matches) {
        if (drawer.classList.contains('is-open') && typeof drawer.close === 'function') drawer.close();
        panel.removeAttribute('role');
        panel.removeAttribute('aria-modal');
        drawer.removeAttribute('aria-hidden');
      } else {
        panel.setAttribute('role', 'dialog');
        panel.setAttribute('aria-modal', 'true');
        if (!drawer.classList.contains('is-open')) drawer.setAttribute('aria-hidden', 'true');
      }
    }

    ownsControl(el) {
      const form = this.form;
      return Boolean(form && el && el.form === form);
    }

    onChange(event) {
      const target = event.target;
      if (!this.ownsControl(target) || target.matches('[data-price-input]')) return;
      this.render(this.formUrl());
    }

    onInput(event) {
      if (!this.ownsControl(event.target) || !event.target.matches('[data-price-input]')) return;
      clearTimeout(this.priceTimer);
      this.priceTimer = setTimeout(() => this.render(this.formUrl(), { keepValue: true }), 500);
    }

    onSubmit(event) {
      if (event.target !== this.form) return;
      event.preventDefault();
      clearTimeout(this.priceTimer);
      this.render(this.formUrl());
    }

    onClick(event) {
      const link = event.target.closest('a[data-facet-link]');
      if (!link || event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
      event.preventDefault();
      const url = new URL(link.href, window.location.origin);
      this.render(url.pathname + url.search);
    }

    onPopState() {
      const path = this.formPath();
      if (window.location.pathname !== path) return;
      this.render(window.location.pathname + window.location.search, { push: false });
    }

    formPath() {
      const form = this.form;
      if (!form) return window.location.pathname;
      return new URL(form.getAttribute('action') || window.location.pathname, window.location.origin).pathname;
    }

    formUrl() {
      const params = new URLSearchParams();
      for (const [key, value] of new FormData(this.form)) {
        if (String(value).trim() !== '') params.append(key, value);
      }
      const query = params.toString();
      return query ? `${this.formPath()}?${query}` : this.formPath();
    }

    currentKey() {
      return window.location.pathname + window.location.search;
    }

    sectionUrl(url) {
      const joiner = url.includes('?') ? '&' : '?';
      return `${url}${joiner}section_id=${encodeURIComponent(this.sectionId)}`;
    }

    async render(url, { push = true, keepValue = false } = {}) {
      /* Only ever fetch or navigate within this store; a path like //other.site must not leave it. */
      if (new URL(url, window.location.origin).origin !== window.location.origin) return;
      clearTimeout(this.priceTimer);
      if (this.controller) this.controller.abort();
      const controller = new AbortController();
      this.controller = controller;

      const results = this.querySelector('[data-facet-results]');
      if (results) {
        results.classList.add('is-loading');
        results.setAttribute('aria-busy', 'true');
      }

      try {
        let html = this.cache.get(url);
        if (!html) {
          const response = await fetch(this.sectionUrl(url), { signal: controller.signal, headers: { Accept: 'text/html' } });
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          html = await response.text();
          this.cache.set(url, html);
        }
        if (controller.signal.aborted) return;
        this.swap(html, keepValue);
        if (push && url !== this.currentKey()) history.pushState({ facets: true }, '', url);
      } catch (error) {
        if (error.name === 'AbortError') return;
        window.location.href = url;
      } finally {
        if (this.controller === controller) {
          this.controller = null;
          if (results) {
            results.classList.remove('is-loading');
            results.removeAttribute('aria-busy');
          }
        }
      }
    }

    /* keepValue: the visitor may still be typing in the price field that triggered this render. */
    swap(html, keepValue = false) {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const active = document.activeElement;
      const focusId = active && this.contains(active) ? active.id : null;
      const liveValue = keepValue && active && active.matches && active.matches('[data-price-input]') ? active.value : null;
      const hadFocusInside = Boolean(active && this.contains(active));

      const openState = new Map();
      this.querySelectorAll('details[id]').forEach((d) => openState.set(d.id, d.open));

      this.querySelectorAll('[data-facet-region]').forEach((region) => {
        const name = region.dataset.facetRegion;
        const next = doc.querySelector(`[data-facet-region="${name}"]`);
        if (next) region.innerHTML = next.innerHTML;
      });

      this.querySelectorAll('details[id]').forEach((d) => {
        if (openState.has(d.id)) d.open = openState.get(d.id);
      });

      if (window.themeMotion && typeof window.themeMotion.reveals === 'function') window.themeMotion.reveals(this);

      if (!hadFocusInside) return;
      let target = focusId ? document.getElementById(focusId) : null;
      if (target && liveValue !== null) target.value = liveValue;
      if (!target || target.offsetParent === null) {
        target = this.drawer && this.drawer.classList.contains('is-open')
          ? this.drawer.querySelector('[data-facets-panel]')
          : this.querySelector('[data-facet-results]');
      }
      if (target) target.focus({ preventScroll: true });
    }
  }

  customElements.define('facet-form', FacetForm);
})();
